import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserCheck, CreditCard, DollarSign, Eye, Activity, RefreshCw, ShieldCheck,
  KeyRound, Copy, AlertCircle, Sparkles, Crown, UserMinus, Upload, Trash2, Briefcase
} from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?limit=50"),
      ]);
      setStats(s.data);
      setUsers(u.data || []);
    } catch (e) {
      // surfaced via toast if needed
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  // Revenue breakdown by currency (multi-currency safe)
  const rbc = stats?.revenue_by_currency || [];
  const currencySymbol = (c) => ({
    USD: "$", EUR: "€", GBP: "£", INR: "₹", AED: "د.إ", SAR: "﷼", QAR: "﷼",
    AUD: "A$", CAD: "C$", JPY: "¥", CNY: "¥",
  }[String(c).toUpperCase()] || `${c} `);
  const revenueLabel = rbc.length === 0
    ? "—"
    : rbc.length === 1
      ? `${currencySymbol(rbc[0].currency)}${rbc[0].total.toLocaleString()}`
      : `${currencySymbol(rbc[0].currency)}${rbc[0].total.toLocaleString()} +${rbc.length - 1}`;
  const revenueSub = rbc.length <= 1
    ? "All-time"
    : rbc.map((r) => `${currencySymbol(r.currency)}${r.total.toLocaleString()}`).join(" · ");

  const cards = [
    { label: "Total visits", value: stats?.total_visits ?? 0, icon: Eye, color: "var(--cavilo-blue)", bg: "var(--cavilo-blue-light)", sub: `${stats?.unique_visitors ?? 0} unique` },
    { label: "Registered users", value: stats?.total_users ?? 0, icon: Users, color: "var(--cavilo-teal-deep)", bg: "var(--cavilo-teal-light)", sub: `${stats?.users_active_last_7d ?? 0} active this week` },
    { label: "Logged in (30d)", value: stats?.users_active_last_30d ?? 0, icon: UserCheck, color: "#10B981", bg: "#D1FAE5", sub: "Recent activity" },
    { label: "Premium users", value: stats?.premium_users ?? 0, icon: CreditCard, color: "#8B5CF6", bg: "#EDE9FE", sub: `${stats?.paid_transactions ?? 0} paid txns` },
    { label: "Revenue", value: revenueLabel, icon: DollarSign, color: "#F59E0B", bg: "#FEF3C7", sub: revenueSub },
    { label: "Visits last 30d", value: stats?.visits_last_30d ?? 0, icon: Activity, color: "#0EA5E9", bg: "#E0F2FE", sub: "Rolling window" },
  ];

  return (
    <div className="space-y-8">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip mb-3"><ShieldCheck size={12} /> Admin</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Cavilow control room</h1>
          <p className="mt-2 text-slate-500 max-w-xl">Visits, sign-ups, subscriptions, and revenue — across every Cavilow user.</p>
        </div>
        <button
          data-testid="admin-refresh"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-2xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-5 py-2.5 text-sm font-bold shadow-lg shadow-blue-500/20 disabled:opacity-60"
        >
          <RefreshCw size={16} /> {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 fade-up-delay-1">
        {cards.map((c) => (
          <div key={c.label} className="glass p-5 hover-lift relative overflow-hidden">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-30" style={{ background: c.bg }} />
            <div className="relative flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.label}</div>
              <span className="grid place-items-center w-9 h-9 rounded-2xl" style={{ background: c.bg, color: c.color }}>
                <c.icon size={16} />
              </span>
            </div>
            <div className="relative mt-2 font-display font-black text-4xl leading-none text-white">{c.value}</div>
            <div className="relative mt-2 text-xs text-slate-500 truncate">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Daily activity */}
      <div className="grid lg:grid-cols-2 gap-6 fade-up-delay-2">
        <Mini bars title="Visits — last 7 days" data={stats?.visits_by_day || []} color="var(--cavilo-blue)" />
        <Mini bars title="New users — last 7 days" data={stats?.new_users_by_day || []} color="var(--cavilo-teal-deep)" />
      </div>

      {/* Password reset tool */}
      <PasswordResetCard />

      {/* Manual subscription tool */}
      <SubscriptionCard />

      {/* Real jobs upload */}
      <JobsUploadCard />

      {/* Weekly digest trigger */}
      <DigestTriggerCard />

      {/* Users table */}
      <div className="glass overflow-hidden fade-up-delay-3">
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="font-display font-bold">Recent users</div>
          <div className="text-xs text-slate-500">{users.length} shown</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Email</th>
                <th className="text-left px-6 py-3 font-bold">Name</th>
                <th className="text-left px-6 py-3 font-bold">Country</th>
                <th className="text-left px-6 py-3 font-bold">Plan</th>
                <th className="text-left px-6 py-3 font-bold">Created</th>
                <th className="text-left px-6 py-3 font-bold">Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-white/8">
                  <td className="px-6 py-3 font-mono text-xs">
                    {u.is_admin && <span className="chip chip-amber mr-2" style={{ padding: "2px 8px" }}>Admin</span>}
                    {u.email}
                  </td>
                  <td className="px-6 py-3">{u.name || "—"}</td>
                  <td className="px-6 py-3">{u.country || "—"} <span className="text-slate-500">{u.currency || ""}</span></td>
                  <td className="px-6 py-3">
                    <span className={`chip ${u.subscription === "premium" ? "chip-mint" : ""}`} style={{ padding: "2px 8px" }}>
                      {u.subscription || "free"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-500 text-xs">{(u.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-6 py-3 text-slate-500 text-xs">{(u.last_login_at || "").slice(0, 10) || "—"}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">No users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SubscriptionCard() {
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("premium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const verb = plan === "premium" ? "GRANT premium access to" : "REVOKE premium and downgrade";
    if (!window.confirm(`${verb} ${email}?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/set-subscription", { email: email.trim().toLowerCase(), plan });
      setResult(r.data);
      toast.success(`${r.data.email} is now ${r.data.plan.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-subscription"
      className="glass p-6 fade-up-delay-2"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[rgba(128,255,234,0.1)] text-emerald-600 shrink-0">
          <Crown size={20} />
        </span>
        <div>
          <div className="font-display font-bold text-lg text-white">Subscription override</div>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Manually grant or revoke premium for a user — for cases where Stripe paid but the webhook didn&apos;t deliver,
            or to comp a user.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch" data-testid="admin-sub-form">
        <input
          data-testid="admin-sub-email"
          type="email"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 rounded-xl border-2 border-white/10 px-4 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)]"
          required
        />
        <select
          data-testid="admin-sub-plan"
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="rounded-xl border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF] bg-white/5 text-white"
        >
          <option value="premium">→ Premium</option>
          <option value="free">→ Free</option>
        </select>
        <button
          data-testid="admin-sub-submit"
          type="submit"
          disabled={busy || !email.trim()}
          className={`inline-flex items-center justify-center gap-2 rounded-xl text-white px-5 py-2.5 text-sm font-bold shadow-lg btn-press disabled:opacity-60 ${
            plan === "premium"
              ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/25"
              : "bg-slate-600 hover:bg-slate-700 shadow-slate-500/20"
          }`}
        >
          {plan === "premium" ? <Sparkles size={14} /> : <UserMinus size={14} />}
          {busy ? "Updating…" : plan === "premium" ? "Grant premium" : "Downgrade to free"}
        </button>
      </form>

      {result && (
        <div
          data-testid="admin-sub-result"
          className="mt-4 rounded-2xl border-2 border-[rgba(128,255,234,0.25)] bg-[rgba(128,255,234,0.1)]/60 p-4 text-sm text-slate-200"
        >
          <b>{result.email}</b> is now <b className="text-[#80FFEA]">{result.plan.toUpperCase()}</b>.
          {result.name && <> · {result.name}</>}
        </div>
      )}
    </div>
  );
}

function PasswordResetCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!window.confirm(`Generate a new temporary password for ${email}? Their current password will stop working and the temp password will be emailed to them.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/reset-password", { email: email.trim().toLowerCase() });
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success(`Temp password generated and emailed to ${r.data.email}`);
      } else {
        toast.warning("Temp password generated, but the email could NOT be sent. Share it manually.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const reset = () => { setEmail(""); setResult(null); };

  return (
    <div
      data-testid="admin-password-reset"
      className="glass p-6 fade-up-delay-2"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="grid place-items-center w-11 h-11 rounded-2xl bg-amber-50 text-amber-600 shrink-0">
          <KeyRound size={20} />
        </span>
        <div>
          <div className="font-display font-bold text-lg text-white">Password reset</div>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Generate a temporary password for a user when they request a reset.
            Share it with them by email — they can change it from Settings after signing in.
          </p>
        </div>
      </div>

      {!result ? (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="admin-reset-form">
          <input
            data-testid="admin-reset-email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-xl border-2 border-white/10 px-4 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)]"
            required
          />
          <button
            data-testid="admin-reset-submit"
            type="submit"
            disabled={busy || !email.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-5 py-2.5 text-sm font-bold shadow-lg shadow-blue-500/20 disabled:opacity-60 btn-press"
          >
            <KeyRound size={14} /> {busy ? "Generating…" : "Generate temporary password"}
          </button>
        </form>
      ) : (
        <div
          data-testid="admin-reset-result"
          className="rounded-2xl border-2 border-[rgba(128,255,234,0.25)] bg-[rgba(128,255,234,0.1)]/60 p-5"
        >
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-300 leading-relaxed">
              {result.email_sent ? (
                <><b>Email sent ✉️</b> — the user has received this temporary password by email.
                  Their old password no longer works. Keep this code handy in case they need it re-shared.</>
              ) : (
                <><b>⚠️ Email could not be sent</b> — copy this code and share it with the user manually.
                  Their old password no longer works.</>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">User</div>
              <div className="font-mono text-sm text-white">{result.email}</div>
              {result.name && <div className="text-xs text-slate-500 mt-0.5">{result.name}</div>}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Temporary password</div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="admin-reset-temp"
                  className="flex-1 font-mono text-sm bg-white/5 border border-[rgba(128,255,234,0.25)] rounded-xl px-3 py-2 text-white select-all"
                >
                  {result.temp_password}
                </code>
                <button
                  data-testid="admin-reset-copy"
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 btn-neon btn-press"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
          </div>
          <button
            data-testid="admin-reset-done"
            type="button"
            onClick={reset}
            className="text-xs font-semibold text-slate-500 hover:text-white"
          >
            ← Reset another user
          </button>
        </div>
      )}
    </div>
  );
}

function Mini({ title, data, color }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="glass p-6">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">{title}</div>
      <div className="flex items-end gap-2 h-32">
        {data.length === 0 && <div className="text-sm text-slate-500">No data yet</div>}
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full rounded-t-lg" style={{ height: `${(d.count / max) * 100}%`, background: color, minHeight: 2 }} />
            <div className="text-[9px] text-slate-500 font-mono">{d.date.slice(5)}</div>
            <div className="text-xs font-bold text-white">{d.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function JobsUploadCard() {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [jsonText, setJsonText] = useState("");
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/jobs?limit=200");
      setJobs(r.data?.jobs || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const uploadCsv = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/jobs/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.accepted} accepted, ${r.data.rejected} rejected`);
      setFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const uploadJson = async () => {
    if (!jsonText.trim()) { toast.error("Paste jobs JSON first"); return; }
    setBusy(true); setResult(null);
    try {
      let payload;
      const parsed = JSON.parse(jsonText);
      payload = Array.isArray(parsed) ? { jobs: parsed } : (parsed.jobs ? parsed : { jobs: [parsed] });
      const r = await api.post("/admin/jobs/upload", payload);
      setResult(r.data);
      toast.success(`${r.data.accepted} accepted, ${r.data.rejected} rejected`);
      setJsonText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (jid) => {
    if (!window.confirm("Delete this job from Cavilow?")) return;
    try {
      await api.delete(`/admin/jobs/${jid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const sample = `[
  {
    "title": "Senior Frontend Engineer",
    "company": "Acme Inc.",
    "location": "Dubai, UAE",
    "country": "UAE",
    "industry": "SaaS",
    "seniority": "senior",
    "years_min": 5,
    "salary_range": "AED 25,000–35,000",
    "skills": ["React", "TypeScript", "Node.js"],
    "apply_url": "https://acme.com/careers/senior-fe",
    "source": "acme.com",
    "posted_at": "2026-02-20"
  }
]`;

  return (
    <div className="glass p-6 space-y-5" data-testid="admin-jobs-upload">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Briefcase size={12} /> Job Agent · Real jobs</div>
          <div className="font-display font-bold text-lg">Upload verified jobs</div>
          <div className="text-xs text-slate-500 mt-1">
            Placeholder URLs (example.com, localhost, dummy) are rejected automatically. Only jobs with valid apply links are stored and shown.
          </div>
        </div>
        <div className="text-xs font-bold text-slate-500 bg-white/5 rounded-full px-3 py-1" data-testid="admin-jobs-count">
          {jobs.length} live job{jobs.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* CSV upload */}
        <div className="rounded-2xl border border-white/8 p-4 space-y-3">
          <div className="font-bold text-sm flex items-center gap-2"><Upload size={14} /> CSV upload</div>
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Columns: <code>job_title, company, location, country, industry, seniority, salary_range, skills_required, experience_required, apply_url, source, published_date</code>
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid="admin-jobs-csv-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs w-full"
          />
          <button
            data-testid="admin-jobs-csv-upload"
            disabled={busy || !file}
            onClick={uploadCsv}
            className="w-full btn-neon disabled:opacity-60"
          >
            {busy ? "Uploading…" : "Upload CSV"}
          </button>
        </div>

        {/* JSON paste */}
        <div className="rounded-2xl border border-white/8 p-4 space-y-3">
          <div className="font-bold text-sm flex items-center gap-2"><Upload size={14} /> JSON paste</div>
          <textarea
            data-testid="admin-jobs-json"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            rows={6}
            placeholder={sample}
            className="w-full rounded-xl border border-white/10 px-3 py-2 text-xs font-mono resize-none focus:outline-none focus:border-[var(--cavilo-blue)]"
          />
          <button
            data-testid="admin-jobs-json-upload"
            disabled={busy || !jsonText.trim()}
            onClick={uploadJson}
            className="w-full btn-neon disabled:opacity-60"
          >
            {busy ? "Uploading…" : "Upload JSON"}
          </button>
        </div>
      </div>

      {result && (
        <div className="rounded-2xl bg-[rgba(128,255,234,0.1)] border border-[rgba(128,255,234,0.25)] p-3 text-xs" data-testid="admin-jobs-result">
          <div className="font-bold text-emerald-800">Result: {result.accepted} accepted · {result.rejected} rejected</div>
          {result.rejected_details?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {result.rejected_details.map((r, i) => (
                <li key={i} className="text-emerald-900/80">• {r.title || "?"} @ {r.company || "?"} — <span className="font-mono">{r.reason}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Existing jobs table */}
      <div className="rounded-2xl border border-white/8 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between">
          <div className="font-bold text-sm">Live jobs in Cavilow</div>
          <button
            data-testid="admin-jobs-refresh"
            onClick={load}
            className="text-xs font-bold text-slate-500 hover:text-white inline-flex items-center gap-1"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Title</th>
                <th className="text-left px-3 py-2 font-bold">Company</th>
                <th className="text-left px-3 py-2 font-bold">Location</th>
                <th className="text-left px-3 py-2 font-bold">Source</th>
                <th className="text-left px-3 py-2 font-bold">Apply</th>
                <th className="text-left px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} className="border-t border-white/8" data-testid={`admin-job-row-${j.job_id}`}>
                  <td className="px-3 py-2 font-semibold">{j.title}</td>
                  <td className="px-3 py-2">{j.company}</td>
                  <td className="px-3 py-2 text-slate-500">{j.location || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{j.source || "—"}</td>
                  <td className="px-3 py-2 truncate max-w-[220px]">
                    <a href={j.apply_url} target="_blank" rel="noreferrer" className="text-[#80FFEA] hover:underline">{j.apply_url}</a>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      data-testid={`admin-job-delete-${j.job_id}`}
                      onClick={() => remove(j.job_id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[rgba(255,0,127,0.25)] text-[#FF6DAE] px-2 py-1 text-[11px] font-bold hover:bg-[rgba(255,0,127,0.1)]"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No live jobs yet. Upload a CSV or paste JSON above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DigestTriggerCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Send the weekly digest to every eligible premium user right now?")) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/send-weekly-digests");
      setResult(r.data);
      toast.success(`Sent to ${r.data.sent} · skipped ${r.data.skipped}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="glass p-6 space-y-3" data-testid="admin-digest-card">
      <div className="chip mb-1"><Sparkles size={12} /> Match Alerts · Weekly digest</div>
      <div className="font-display font-bold text-lg">Trigger the weekly digest now</div>
      <div className="text-xs text-slate-500 leading-relaxed">
        Sends the 3-new + 2-best-fit digest to every premium user with alerts enabled whose last digest was &gt;6 days ago.
        The scheduler also runs automatically each hour at :01 UTC.
      </div>
      <button
        data-testid="admin-digest-send"
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-2 btn-neon disabled:opacity-60"
      >
        <Upload size={14} /> {busy ? "Sending…" : "Send now"}
      </button>
      {result && (
        <div className="rounded-2xl bg-[rgba(128,255,234,0.1)] border border-[rgba(128,255,234,0.25)] p-3 text-xs" data-testid="admin-digest-result">
          <div className="font-bold text-emerald-800">Sent: {result.sent} · Skipped: {result.skipped}</div>
          <div className="text-emerald-900/70 mt-1">Run at {result.at}</div>
        </div>
      )}
    </div>
  );
}

